import { type RefObject, useCallback, useEffect, useState } from 'react'

export interface IMousePosition {
	x: number
	y: number
	percentX: number
	percentY: number
}

export function useMouseMove<T extends HTMLElement>(
	ref?: RefObject<T>,
): [IMousePosition, (event: MouseEvent) => void] {
	const [mousePosition, setMousePosition] = useState<IMousePosition>({
		x: 0,
		y: 0,
		percentX: 0,
		percentY: 0,
	})

	function calculateMousePosition({
		x,
		y,
		width,
		height,
	}: {
		x: number
		y: number
		width: number
		height: number
	}) {
		const percentX = (x / width) * 100
		const percentY = (y / height) * 100

		setMousePosition({
			x,
			y,
			percentX,
			percentY,
		})
	}

	// Window based mouse move position
	useEffect(() => {
		if (ref || typeof window === 'undefined') return

		const handleGlobalMouseMove = (event: MouseEvent) => {
			calculateMousePosition({
				x: event.clientX,
				y: event.clientY,
				width: window.innerWidth,
				height: window.innerHeight,
			})
		}

		window.addEventListener('mousemove', handleGlobalMouseMove)

		return () => {
			window.removeEventListener('mousemove', handleGlobalMouseMove)
		}
	}, [ref])

	// Container based mouse position
	const handleMouseMove = useCallback(
		(event: MouseEvent) => {
			if (!ref) return
			const container = ref.current
			if (!container) return

			const rect = container.getBoundingClientRect()

			calculateMousePosition({
				x: event.clientX - rect.left,
				y: event.clientY - rect.top,
				width: rect.width,
				height: rect.height,
			})
		},
		[ref],
	)

	return [mousePosition, handleMouseMove]
}
